import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, RefreshCw, Search, UserMinus, Users } from "lucide-react";
import { BuyAdminPerformerTeamSearchResult, buyAdminApi } from "../../../services/buyAdminApi";
import styles from "./MasterData.module.css";

const PAGE_SIZE = 25;

const PerformerTeamManagement = ({ refreshKey = 0 }: { refreshKey?: number }) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [rows, setRows] = useState<BuyAdminPerformerTeamSearchResult[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const loadResults = useCallback(async (searchQuery: string, nextPage: number) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await buyAdminApi.getPerformerTeams({
        q: searchQuery || undefined,
        page: nextPage,
        per_page: PAGE_SIZE,
      });
      setRows(response.data || []);
      setTotal(response.pagination?.total || 0);
      setTotalPages(Math.max(1, response.pagination?.total_pages || 1));
      setSelectedIds([]);
    } catch (err: any) {
      setError(err?.message || "Failed to load performers");
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadResults(debouncedQuery, page);
  }, [debouncedQuery, loadResults, page, refreshKey]);

  const allSelected = rows.length > 0 && selectedIds.length === rows.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(rows.map((row) => row.id));
  };

  const toggleRow = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const selectedCountLabel = useMemo(() => {
    if (!selectedIds.length) return "";
    return `${selectedIds.length} selected`;
  }, [selectedIds.length]);

  const visibleCount = useMemo(() => rows.length, [rows.length]);

  const runBulkUpdate = async (isTeam: boolean) => {
    if (!selectedIds.length) return;

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = await buyAdminApi.bulkUpdatePerformerTeams({
        performerIds: selectedIds,
        is_team: isTeam,
      });
      const { updated, failed } = response.data;
      if (failed.length) {
        setError(`${updated} updated, ${failed.length} failed. ${failed.map((f) => `#${f.id}: ${f.reason}`).join(" ")}`);
      } else {
        setSuccess(`${updated} performer${updated === 1 ? "" : "s"} ${isTeam ? "marked as team" : "removed from teams"}.`);
      }
      setSelectedIds([]);
      await loadResults(debouncedQuery, page);
    } catch (err: any) {
      setError(err?.message || "Failed to update performer teams");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>Performer Teams</h2>
      </div>

      <p className={styles.sectionDesc}>
        Browse Ticket Evolution performers and bulk update <code>is_team</code>. Category is taken from TE automatically.
      </p>

      <div className={styles.searchRow}>
        <Search size={18} className={styles.searchIcon} />
        <input
          className={styles.searchInput}
          type="search"
          placeholder="Search performers..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {loading ? (
        <div className={styles.searchLoader} aria-live="polite">
          <RefreshCw size={16} className={styles.spin} />
          Loading performers...
        </div>
      ) : null}

      {error ? (
        <div className={styles.errorBanner}>
          <AlertCircle size={16} />
          {error}
        </div>
      ) : null}

      {success ? (
        <div className={styles.successBanner}>
          <CheckCircle2 size={16} />
          {success}
        </div>
      ) : null}

      {selectedIds.length > 0 ? (
        <div className={styles.bulkBar}>
          <span>{selectedCountLabel}</span>
          <div className={styles.bulkActions}>
            <button
              type="button"
              className={styles.actionButton}
              disabled={saving}
              onClick={() => void runBulkUpdate(true)}
            >
              <Users size={16} />
              {saving ? "Saving..." : "Mark as Team"}
            </button>
            <button
              type="button"
              className={styles.dangerButton}
              disabled={saving}
              onClick={() => void runBulkUpdate(false)}
            >
              <UserMinus size={16} />
              {saving ? "Saving..." : "Remove Team"}
            </button>
          </div>
        </div>
      ) : null}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  disabled={!rows.length || loading}
                  aria-label="Select all performers"
                />
              </th>
              <th>ID</th>
              <th>Name</th>
              <th>Category</th>
              <th>Is Team</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className={styles.checkboxCell}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleRow(row.id)}
                    aria-label={`Select ${row.name}`}
                  />
                </td>
                <td>{row.id}</td>
                <td>{row.name}</td>
                <td>{row.category_name ?? "-"}</td>
                <td>
                  <span className={`${styles.badge} ${row.is_team === true ? styles.badgeYes : styles.badgeNo}`}>
                    {row.is_team === true ? "Yes" : "Unset"}
                  </span>
                </td>
              </tr>
            ))}
            {!rows.length && !loading ? (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  No performers found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className={styles.footer}>
        <span>
          Showing {visibleCount} of {total.toLocaleString()} performers
        </span>
        <div className={styles.pagination}>
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Prev
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
};

export default PerformerTeamManagement;
